/**
 * Coupon copy that cannot contradict the coupon.
 *
 * A coupon's name and description are free text held in `name_i18n` /
 * `desc_i18n`, written by whoever configured the template. The amount it
 * actually pays comes from `bonus_type` + `bonus_value` and is computed on the
 * server. Nothing joins the two, so an edit to the value that misses the text
 * leaves the app advertising one rate and paying another — which is what
 * happened to `first_deposit_1`: the name still said "+30%" long after the
 * value had become 0.10, so a R$ 100 deposit was offered "+30%" and credited
 * R$ 10.
 *
 * The rate shown to a player is therefore derived from the numbers, never read
 * out of the text. Percentages already written into a name or description are
 * rewritten to match. Configuration can still be wrong about *which* coupon a
 * player gets — that is an ops decision — but it can no longer be wrong about
 * what this coupon pays.
 */

export type CouponRate = {
  bonusType?: string;
  bonusValue?: number;
};

/** Any number followed by a percent sign, with or without decimals. */
const PERCENT_TOKEN = /\d+(?:[.,]\d+)?\s*%/g;

function formatPercent(value: number, lang: string): string {
  // Rates are whole numbers today (30/20/15/10). Format through Intl anyway so
  // a fractional rate reads correctly in pt-BR, where the separator is a comma.
  const pct = value * 100;
  const text = new Intl.NumberFormat(lang || 'pt-BR', {
    maximumFractionDigits: 2,
  }).format(Number(pct.toFixed(2)));
  return `${text}%`;
}

/**
 * The rate this coupon really pays, as a display string — `10%` for a
 * percentage coupon, `null` for anything else (a fixed coupon has an amount,
 * not a rate, and its amount is already shown beside it).
 */
export function couponRate(coupon: CouponRate, lang = 'pt-BR'): string | null {
  if (coupon.bonusType !== 'percent') return null;
  const value = Number(coupon.bonusValue || 0);
  if (!(value > 0)) return null;
  return formatPercent(value, lang);
}

/**
 * Return `text` with every percentage in it replaced by the coupon's real rate.
 *
 * Left untouched when there is no rate to compare against (a fixed coupon) or
 * when the text quotes no percentage — this corrects copy, it does not invent
 * it. Callers still show the exact money amount separately; this only stops the
 * headline from disagreeing with it.
 */
export function withTrueRate(text: string, coupon: CouponRate, lang = 'pt-BR'): string {
  if (!text) return text;
  const rate = couponRate(coupon, lang);
  if (!rate) return text;
  return text.replace(PERCENT_TOKEN, rate);
}

/**
 * The headline for a coupon — `+10%` or `+R$ 20` — built only from the numbers.
 * `formatMoney` is injected so this module stays free of currency concerns.
 */
export function couponHeadline(
  coupon: CouponRate,
  formatMoney: (value: number) => string,
  lang = 'pt-BR',
): string {
  if (coupon.bonusType === 'fixed') return `+${formatMoney(Number(coupon.bonusValue || 0))}`;
  const rate = couponRate(coupon, lang);
  return rate ? `+${rate}` : '';
}
